import type { VisualShape } from 'types'
import type { FunnelStage } from '../core/types/funnel-stage.js'

export const AGENT_SHAPES = new Set<VisualShape>([
  'discovery',
  'scoring',
  'workshop',
  'consulting',
  'closer',
  'summary',
  'proposal',
  'admin',
  'retargeting',
  'neural_network',
])

export const MULTIMODAL_SHAPES = new Set<VisualShape>(['webcam', 'screenshare'])

export const PRESENCE_SHAPES = new Set<VisualShape>([
  'orb',
  'wave',
  'network',
  'constellation',
  'scanner',
  'grid',
  'shield',
])

export const CHAT_PRESENCE_SHAPES = new Set<VisualShape>([
  ...PRESENCE_SHAPES,
  'screenshare',
  'webcam',
])

export const CHAT_DATA_SHAPES = new Set<VisualShape>(['weather', 'map', 'chart'])

export const CHAT_TOOL_SHAPES = new Set<VisualShape>([
  'code',
  'scanner',
  'screenshare',
  'webcam',
  'functionPulse',
  'toolCall',
])

export const RESEARCH_ANIMATION_SHAPES = new Set<VisualShape>([
  'network',
  'molecule',
  'neural_network',
])

export const CHAT_RESEARCH_ANIM_SHAPES = RESEARCH_ANIMATION_SHAPES

export const ACTION_PRIORITY: Record<VisualShape, number> = {
  error: 110,
  screenshare: 100,
  webcam: 96,
  functionPulse: 88,
  weather: 82,
  chart: 80,
  map: 78,
  code: 76,
  text: 76,
  wave: 74,
  oscilloscope: 72,
  spectrum: 72,
  constellation: 70,
  orb: 68,
  discovery: 62,
  scoring: 60,
  workshop: 58,
  consulting: 58,
  closer: 56,
  proposal: 56,
  summary: 56,
  admin: 54,
  retargeting: 52,
  toolCall: 50,
  brain: 48,
  shield: 46,
  scanner: 44,
  solarSystem: 42,
  planet: 40,
  network: 38,
  molecule: 36,
  neural_network: 36,
  mathGraph: 34,
  grid: 32,
  rect: 30,
  dna: 28,
  heart: 26,
  atom: 24,
  hourglass: 22,
  star: 20,
  globe: 20,
  clock: 18,
  vortex: 16,
  fireworks: 14,
  lightning: 14,
  flower: 12,
}

export const SHAPE_CATEGORIES: Record<string, VisualShape[]> = {
  geometric: ['orb', 'wave', 'dna', 'grid', 'heart', 'flower', 'rect'],
  cosmic: ['planet', 'atom', 'star', 'globe', 'solarSystem', 'vortex'],
  agent: [
    'discovery',
    'scoring',
    'workshop',
    'consulting',
    'closer',
    'summary',
    'proposal',
    'admin',
    'retargeting',
  ],
  data: ['chart', 'map', 'weather', 'network', 'molecule', 'mathGraph', 'neural_network'],
  ui: ['clock', 'code', 'text', 'scanner', 'error', 'toolCall', 'functionPulse', 'screenshare', 'webcam'],
  misc: ['brain', 'constellation', 'fireworks', 'lightning', 'shield', 'hourglass'],
}

export const agentToShape: Record<string, VisualShape> = {
  'Discovery Agent': 'discovery',
  'Scoring Agent': 'scoring',
  'Workshop Sales Agent': 'workshop',
  'Consulting Sales Agent': 'consulting',
  'Closer Agent': 'closer',
  'Summary Agent': 'summary',
  'Proposal Agent': 'proposal',
  'Admin Agent': 'admin',
  'Retargeting Agent': 'retargeting',
  'Lead Intelligence Agent': 'neural_network',
}

export const stageToShape: Record<FunnelStage, VisualShape> = {
  DISCOVERY: 'discovery',
  QUALIFIED: 'scoring',
  SCORING: 'scoring',
  INTELLIGENCE_GATHERING: 'neural_network',
  WORKSHOP_PITCH: 'workshop',
  CONSULTING_PITCH: 'consulting',
  PITCHING: 'orb',
  PROPOSAL: 'proposal',
  OBJECTION: 'shield',
  CLOSING: 'closer',
  BOOKING_REQUESTED: 'star',
  BOOKED: 'heart',
  SUMMARY: 'summary',
  RETARGETING: 'retargeting',
  ADMIN: 'admin',
  FORCE_EXIT: 'vortex',
}

export function getShapePriority(shape: VisualShape | undefined): number {
  if (!shape) return -1
  return ACTION_PRIORITY[shape] ?? 0
}

export function getStageShape(stage: string | null | undefined): VisualShape {
  if (!stage) return 'orb'
  return stageToShape[stage as FunnelStage] ?? 'orb'
}
